'use client';

import { useEffect, useState } from 'react';
import styles from './style.module.scss';

const MOOVE_CONSENT_COOKIE = 'moove_gdpr_popup';
const LEGACY_CONSENT_COOKIE = 'sin_cookie_consent';
// Moove's default consent expiration is 365 days. Keep this aligned with the
// consent expiration configured in WordPress > GDPR Cookie Compliance.
const CONSENT_MAX_AGE = 60 * 60 * 24 * 365;

function readCookie(name) {
  if (typeof document === 'undefined') return null;
  return document.cookie
    .split('; ')
    .find((cookie) => cookie.startsWith(`${name}=`))
    ?.slice(name.length + 1);
}

function parseMooveConsent(value) {
  if (!value) return null;
  try {
    let decodedValue = value;
    try {
      decodedValue = decodeURIComponent(value);
    } catch {
      // Accept already-decoded values as well as Moove's URL-encoded cookie.
    }
    const moove = JSON.parse(decodedValue);
    if (moove.strict !== '1' && moove.strict !== 1) return null;
    return {
      necessary: true,
      analytics: moove.thirdparty === '1' || moove.thirdparty === 1 || moove.performance === '1' || moove.performance === 1,
      marketing: moove.advanced === '1' || moove.advanced === 1,
      updatedAt: Date.now(),
    };
  } catch {
    return null;
  }
}

function readConsent() {
  return parseMooveConsent(readCookie(MOOVE_CONSENT_COOKIE));
}

function getSharedCookieDomain(cmsHostname) {
  const appParts = window.location.hostname.toLowerCase().split('.').reverse();
  const cmsParts = (cmsHostname || '').toLowerCase().split('.').reverse();
  const sharedParts = [];

  while (appParts[sharedParts.length] && appParts[sharedParts.length] === cmsParts[sharedParts.length]) {
    sharedParts.push(appParts[sharedParts.length]);
  }

  // Avoid setting a cookie on a public suffix such as .com or .net.
  return sharedParts.length >= 3 ? `; Domain=.${sharedParts.reverse().join('.')}` : '';
}

function writeMooveConsent(consent, cmsHostname) {
  const mooveConsent = {
    strict: '1',
    thirdparty: consent.analytics ? '1' : '0',
    advanced: consent.marketing ? '1' : '0',
    performance: '0',
    preference: '0',
  };
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  const domain = getSharedCookieDomain(cmsHostname);

  document.cookie = `${MOOVE_CONSENT_COOKIE}=${encodeURIComponent(JSON.stringify(mooveConsent))}; Max-Age=${CONSENT_MAX_AGE}; Path=/; SameSite=Lax${secure}${domain}`;

  // Keep Moove's documented client-side consent variables and GTM event in
  // sync for scripts configured through WordPress integrations.
  window.gdpr_consent__strict = 'true';
  window.gdpr_consent__thirdparty = consent.analytics ? 'true' : 'false';
  window.gdpr_consent__advanced = consent.marketing ? 'true' : 'false';
  window.gdpr_consent__performance = 'false';
  window.gdpr_consent__preference = 'false';
  window.gdpr_consent__cookies = [
    'strict',
    ...(consent.analytics ? ['thirdparty'] : []),
    ...(consent.marketing ? ['advanced'] : []),
  ].join('|');

  if (window.dataLayer && typeof window.dataLayer.push === 'function') {
    window.dataLayer.push({
      event: 'cookie_consent_update',
      gdpr_consent__cookies: window.gdpr_consent__cookies,
      gdpr_consent__strict: window.gdpr_consent__strict,
      gdpr_consent__thirdparty: window.gdpr_consent__thirdparty,
      gdpr_consent__advanced: window.gdpr_consent__advanced,
      gdpr_consent__performance: window.gdpr_consent__performance,
      gdpr_consent__preference: window.gdpr_consent__preference,
    });
  }

  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', {
      analytics_storage: consent.analytics ? 'granted' : 'denied',
      ad_storage: consent.marketing ? 'granted' : 'denied',
      ad_user_data: consent.marketing ? 'granted' : 'denied',
      ad_personalization: consent.marketing ? 'granted' : 'denied',
      functionality_storage: 'granted',
      security_storage: 'granted',
      personalization_storage: 'denied',
    });
  }

  window.dispatchEvent(new CustomEvent('cookie_consent_update', { detail: mooveConsent }));

  if (typeof window.wp_set_consent === 'function') {
    window.wp_set_consent('functional', 'allow');
    window.wp_set_consent('statistics', consent.analytics ? 'allow' : 'deny');
    window.wp_set_consent('marketing', consent.marketing ? 'allow' : 'deny');
    window.wp_set_consent('preferences', 'deny');
  }
}

function appendCode(code, id, target = document.body) {
  if (!code || document.getElementById(id)) return;

  const template = document.createElement('template');
  template.innerHTML = code.trim();
  const nodes = [...template.content.childNodes];

  if (!nodes.some((node) => node.nodeName === 'SCRIPT')) {
    const script = document.createElement('script');
    script.id = id;
    script.text = code;
    target.appendChild(script);
    return;
  }

  nodes.forEach((node, index) => {
    if (node.nodeName === 'SCRIPT') {
      const script = document.createElement('script');
      [...node.attributes].forEach(({ name, value }) => script.setAttribute(name, value));
      script.id = index === 0 ? id : `${id}-${index}`;
      script.text = node.textContent;
      target.appendChild(script);
    } else if (node.nodeType === Node.ELEMENT_NODE && node.nodeName !== 'NOSCRIPT') {
      target.appendChild(node.cloneNode(true));
    }
  });
}

function scriptCategory(script) {
  const title = script.title || '';
  if (/marketing|advert|facebook|meta pixel|remarket/i.test(title)) return 'marketing';
  if (/analytics|google analytics|matomo|plausible/i.test(title)) return 'analytics';
  return 'both';
}

export default function CookieConsent({ gtmScripthead, headScripts, bodyScripts, footerScripts, cmsHostname }) {
  const [consent, setConsent] = useState(null);
  const [visible, setVisible] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    let saved = readConsent();
    if (!saved) {
      // Migrate preferences made with the previous custom cookie into Moove's
      // cookie format, so returning visitors keep their choice.
      const legacyValue = readCookie(LEGACY_CONSENT_COOKIE);
      if (legacyValue) {
        try {
          const legacy = JSON.parse(decodeURIComponent(legacyValue));
          saved = {
            necessary: true,
            analytics: Boolean(legacy.analytics),
            marketing: Boolean(legacy.marketing),
          };
          writeMooveConsent(saved, cmsHostname);
        } catch {
          saved = null;
        }
      }
    }
    setConsent(saved);
    setAnalytics(Boolean(saved?.analytics));
    setMarketing(Boolean(saved?.marketing));
    setVisible(!saved);
  }, [cmsHostname]);

  useEffect(() => {
    if (!consent) return;
    const groups = [
      { scripts: headScripts, target: document.head, place: 'head' },
      { scripts: bodyScripts, target: document.body, place: 'body' },
      { scripts: footerScripts, target: document.body, place: 'footer' },
    ];

    if (consent.analytics && consent.marketing) {
      // A GTM container can contain analytics and advertising tags, so load it
      // only after both optional categories have been accepted.
      appendCode(gtmScripthead, 'sin-gtm-head', document.head);
    }

    if (consent.analytics || consent.marketing) {
      groups.forEach(({ scripts, target, place }) =>
        (scripts || [])
          .filter((script) => {
            if (script.enabled) return false;
            const category = scriptCategory(script);
            return category === 'both'
              ? consent.analytics && consent.marketing
              : category === 'analytics'
                ? consent.analytics
                : consent.marketing;
          })
          .forEach((script, index) => appendCode(script.code, `sin-analytics-${place}-${index}`, target))
      );
    }
  }, [consent, gtmScripthead, headScripts, bodyScripts, footerScripts]);

  useEffect(() => {
    const openSettings = () => {
      setAnalytics(Boolean(consent?.analytics));
      setMarketing(Boolean(consent?.marketing));
      setSettingsOpen(true);
      setVisible(true);
    };
    window.addEventListener('sin:cookie-settings', openSettings);
    return () => window.removeEventListener('sin:cookie-settings', openSettings);
  }, [consent]);

  function saveConsent(next) {
    const previous = consent;
    writeMooveConsent(next, cmsHostname);
    setConsent(next);
    setVisible(false);
    setSettingsOpen(false);

    // Reload after a changed choice so previously permitted scripts stop
    // running and can be removed before the next page view.
    if (previous && (previous.analytics !== next.analytics || previous.marketing !== next.marketing)) {
      window.location.reload();
    }
  }

  return (
    <>
      {!visible && <button className={styles.settingsLink} onClick={() => window.dispatchEvent(new Event('sin:cookie-settings'))}>Cookie settings</button>}
      {visible && (
        <div className={styles.backdrop} role="presentation">
          <section className={styles.banner} role="dialog" aria-modal="true" aria-labelledby="sin-cookie-title">
            <div>
              <p className={styles.eyebrow}>YOUR PRIVACY</p>
              <h2 id="sin-cookie-title">Cookies &amp; privacy</h2>
              <p className={styles.copy}>
                We use necessary cookies to make this site work. With your permission, we also use analytics
                and marketing cookies. You can change your choice at any time in Cookie settings.
              </p>
              {settingsOpen && (
                <div className={styles.preferences}>
                  <label><input type="checkbox" checked disabled /> Necessary <span>Always on</span></label>
                  <label><input type="checkbox" checked={analytics} onChange={(event) => setAnalytics(event.target.checked)} /> Analytics</label>
                  <label><input type="checkbox" checked={marketing} onChange={(event) => setMarketing(event.target.checked)} /> Marketing</label>
                </div>
              )}
              <a className={styles.policy} href="/privacy-policy">Read our Privacy Policy</a>
            </div>
            <div className={styles.actions}>
              {settingsOpen ? (
                <button className={styles.primary} onClick={() => saveConsent({ necessary: true, analytics, marketing, updatedAt: Date.now() })}>Save choices</button>
              ) : (
                <>
                  <button className={styles.secondary} onClick={() => saveConsent({ necessary: true, analytics: false, marketing: false, updatedAt: Date.now() })}>Reject optional</button>
                  <button className={styles.primary} onClick={() => saveConsent({ necessary: true, analytics: true, marketing: true, updatedAt: Date.now() })}>Accept all</button>
                </>
              )}
              {!settingsOpen && <button className={styles.textButton} onClick={() => setSettingsOpen(true)}>Settings</button>}
            </div>
          </section>
        </div>
      )}
    </>
  );
}
